import cx from 'classnames';
import { createPortal } from 'react-dom';
import { useBlocker } from 'react-router-dom';
import { GovTooltip } from '@gov-design-system-ce/react';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import { useDataBoxesContext } from '@gov-nx/module/data-box/providers/DataBoxesContext';
import { useDataBoxMessageFormContext } from '@gov-nx/module/data-box/providers/MessageFormContext';
import { PageCode } from '@gov-nx/module/page/definitions/codes';
import { Button } from '@gov-nx/ui/web/Button/Button';
import { Dialog } from '@gov-nx/ui/web/Dialog';
import { DataBoxRecipientDetail } from '@gov-nx/ui/web/Form/Custom/DataBoxRecipientDetail';
import { FormInput, FormTextarea } from '@gov-nx/ui/web/Form/FormInput';
import { FormFileUpload } from '@gov-nx/ui/web/Form/Upload/FormFileUpload';
import { Icon } from '@gov-nx/ui/web/Icon/Icon';
import { ButtonGroupLayout } from '@gov-nx/ui/web/Layout/ButtonGroupLayout';
import { LayoutGap } from '@gov-nx/ui/web/Layout/LayoutGap';
import { Space } from '@gov-nx/ui/web/Space/Space';
import { InfoTooltip } from '@gov-nx/ui/web/Tooltips/InfoTooltip';
import { MarkdownRender } from '../../Markdown/MarkdownRender';
import { AdditionalDataList } from './AdditionalDataList';
import { AdditionalDataModal } from './AdditionalDataModal';
import { AttachmentsDropdown } from './AttachmentsDropdown';
import { CancelPrompt } from './CancelPrompt';
import { DataBoxMessageFormSkeleton } from './DataBoxMessageFormSkeleton';
import { DocumentsModal } from './DocumentsModal';
import { FormListItem } from './FormListItem';
import { LowCreditPrompt } from './LowCreditPrompt';
import { RecipientAutocomplete } from './RecipientAutocomplete';
import { SenderDropdown } from './SenderDropdown';

export const DataBoxMessageForm = () => {
	const {
		sender,
		localData,
		canSendForm,
		documents,
		recipient,
		showUploadModal,
		hasAdditionalInfo,
		formIsTouched,
		blockUploadModalClose,
		isLoading,
		controls,
		messagePrice,
		submitMutation,
		onMessageCancelled,
		onSubmit,
		openAdditionalDataModal,
		removeDocument,
		onUploadModalClose,
		onFilesConfirm,
		removeFile,
		fileUploadHook,
	} = useDataBoxMessageFormContext();
	const { handleCreditModalOpen, isCancelPromptOpen } = useDataBoxesContext();

	const blocker = useBlocker(() => {
		if (submitMutation.isLoading || submitMutation.isSuccess) return false;
		if (!formIsTouched) return false;

		isCancelPromptOpen.setTrue();
		return true;
	});

	const { t } = useLocale(PageCode['datove-schranky']);

	if (isLoading) {
		return <DataBoxMessageFormSkeleton />;
	}

	return (
		<form
			autoComplete="off"
			noValidate={true}
			onSubmit={onSubmit}
			className="flex flex-col w-full h-full">
			<div className="flex flex-col gap-(--templates-margin-l) justify-between w-full h-full">
				<div className="overflow-y-auto h-full flex flex-col gap-(--templates-margin-l)">
					<div className="flex flex-col">
						<SenderDropdown />
						{recipient ? (
							<DataBoxRecipientDetail dataBox={recipient} />
						) : (
							<RecipientAutocomplete />
						)}
						<div className="ds-new-message ds-subject pr-0.5">
							<FormInput
								field={{
									name: 'subject',
									size: 'm',
								}}
								label={{
									children: `${t('nova-zprava.predmet')}:`,
									isRequired: true,
								}}
							/>
						</div>
					</div>
					<LayoutGap className="h-[calc(100%-46px)] p-0.5">
						{hasAdditionalInfo ||
						localData.documents?.length ||
						localData.files?.length ? (
							<>
								<div
									className={cx(
										'flex flex-col gap-(--spacing-s-nudge)',
										localData.documents?.length || localData.files?.length
											? 'p-3 rounded bg-(--background-primary-subtlest) [ md:flex-col ]'
											: ''
									)}>
									{localData.documents?.length || localData.files?.length ? (
										<ul className="flex flex-wrap gap-2 gov-list--plain">
											{documents
												?.filter(
													(item) =>
														item.dokumentId &&
														localData.documents?.includes(item.dokumentId)
												)
												.map((document) => (
													<FormListItem
														key={document.dokumentId}
														isFile
														fileSize={document.soubory?.[0].velikost}
														name={document.nazev}
														onRemoveClick={() =>
															removeDocument(document.dokumentId as number)
														}>
														{document.nazev}
													</FormListItem>
												))}
											{localData.files?.map((file) => (
												<FormListItem
													key={file.id}
													isFile
													fileSize={file.size}
													name={file.name}
													onRemoveClick={() => removeFile(file.fileId ?? '')}>
													{file.name}
												</FormListItem>
											))}
										</ul>
									) : null}
									<AttachmentsDropdown />
								</div>
								<div
									className={cx(
										'flex flex-col gap-(--spacing-s-nudge) ml-0.5!',
										hasAdditionalInfo
											? 'p-3 rounded bg-(--background-primary-subtlest) [ md:flex-col ]'
											: ''
									)}>
									<AdditionalDataList />
									<Button
										type="outlined"
										nativeType="button"
										color="primary"
										size="s"
										isExpandedMobile
										onClick={openAdditionalDataModal}
										aria-label={
											hasAdditionalInfo
												? t('nova-zprava.upravit-doplnkove-udaje')
												: t('nova-zprava.pridat-doplnkove-udaje')
										}>
										<span>
											{hasAdditionalInfo
												? t('nova-zprava.upravit-doplnkove-udaje')
												: t('nova-zprava.pridat-doplnkove-udaje')}
										</span>
									</Button>
								</div>
							</>
						) : (
							<div className="flex flex-col gap-(--spacing-s-nudge) [ lg:flex-row ]">
								<AttachmentsDropdown />
								<Button
									color="primary"
									type="outlined"
									size="s"
									nativeType="button"
									isExpandedMobile
									onClick={openAdditionalDataModal}
									className="no-outline-offset"
									aria-label={t('nova-zprava.pridat-doplnkove-udaje')}>
									{t('nova-zprava.pridat-doplnkove-udaje')}
								</Button>
							</div>
						)}
						<div className="ds-textarea">
							<FormTextarea
								label={{
									children: t('nova-zprava.text-zpravy'),
									className: 'sr-only',
								}}
								field={{
									name: 'message',
									rows: 4,
									placeholder: t('nova-zprava.text-zpravy'),
									className: 'no-outline-offset',
								}}
							/>
						</div>

						{messagePrice.showMessagePrice ? (
							<div>
								<p>
									{t('nova-zprava.cena-za-odeslani')}
									<Space />
									<strong>{messagePrice.formattedPrice}</strong>
								</p>
								{messagePrice.isLowCredit ? (
									<div className="flex flex-col gap-1 items-start [ sm:flex-row sm:items-center ] ">
										<span className="gov-color--text-status-error">
											{t('nova-zprava.nedostatecny-kredit')}
										</span>
										<Button
											onClick={() =>
												handleCreditModalOpen(
													sender.databox.datovaSchrankaId as string
												)
											}
											size="m"
											color="error"
											type="link"
											aria-label={t('akce.kredit.dobit')}>
											{t('akce.kredit.dobit')}
										</Button>
									</div>
								) : null}
							</div>
						) : null}

						<ButtonGroupLayout customClasses="p-1">
							<Button
								className="hidden! [ ds:inline-block! ]"
								aria-label={t('nova-zprava.zahodit')}
								type="outlined"
								nativeType="button"
								color="primary"
								size="m"
								isExpandedMobile
								iconStart={
									<Icon
										name="trash"
										type="templates"
									/>
								}
								isDisabled={controls.processLoading}
								onClick={onMessageCancelled}>
								{t('nova-zprava.zahodit')}
							</Button>
							{canSendForm ? (
								<Button
									nativeType="submit"
									isDisabled={controls.processLoading}
									isLoading={controls.processLoading}
									color="primary"
									type="solid"
									size="m"
									isExpandedMobile
									aria-label={t('nova-zprava.odeslat')}
									iconStart={
										<Icon
											name="send-fill"
											type="templates"
										/>
									}>
									{t('nova-zprava.odeslat')}
								</Button>
							) : (
								<GovTooltip placement="top">
									<GovTooltip.Trigger className="border-0">
										<Button
											nativeType="button"
											isDisabled={true}
											isFocusable={false}
											color="primary"
											type="solid"
											size="m"
											isExpandedMobile
											iconStart={
												<Icon
													name="send-fill"
													type="templates"
												/>
											}
											aria-label={t('nova-zprava.odeslat')}>
											{t('nova-zprava.odeslat')}
										</Button>
									</GovTooltip.Trigger>
									<GovTooltip.Content size="s">
										<p>{t('nova-zprava.zprava-musi-obsahovat')}</p>
									</GovTooltip.Content>
								</GovTooltip>
							)}
						</ButtonGroupLayout>
					</LayoutGap>
				</div>
			</div>
			{/* TODO NAKITPO-5998: delete portal when GovDialog supports render to body */}
			{createPortal(
				<>
					<Dialog
						isOpen={showUploadModal}
						onClose={onUploadModalClose}
						blockClose={blockUploadModalClose}
						title={<h3>{t('nova-zprava.nahrat-prilohu-ze-zarizeni')}</h3>}
						footer={
							<Button
								color="primary"
								type="solid"
								size="m"
								nativeType="button"
								isDisabled={!fileUploadHook.filesUploaded}
								onClick={onFilesConfirm}
								aria-label={t('nova-zprava.dokumenty.prilozit-vybrane')}>
								{t('nova-zprava.dokumenty.prilozit-vybrane')}
							</Button>
						}>
						<div>
							<LayoutGap gap="s">
								<FormFileUpload
									fileUploadHook={fileUploadHook}
									field={{ name: 'temporary.files', expanded: true }}
								/>
								<p>
									{t('informace.omezeni-souboru.titul')}
									<InfoTooltip
										content={
											<MarkdownRender>
												{t('informace.omezeni-souboru-detail')}
											</MarkdownRender>
										}
									/>
								</p>
							</LayoutGap>
						</div>
					</Dialog>
					<DocumentsModal />
					<AdditionalDataModal isEdit={hasAdditionalInfo} />
					<CancelPrompt
						isOpen={isCancelPromptOpen.isTrue}
						onConfirm={() => {
							isCancelPromptOpen.setFalse();
							blocker.proceed?.();
						}}
						onCancel={() => {
							isCancelPromptOpen.setFalse();
							blocker.reset?.();
						}}
					/>
					<LowCreditPrompt />
				</>,
				document.body
			)}
		</form>
	);
};
